import type { ReactNode } from 'react';
import { cx } from '../cx';
import { barWidth } from '../ScoreBar/ScoreBar';

export type RankingRowProps = {
  /** Formatted position ("1", "12"). */
  rank: ReactNode;
  /** A LogoTile. */
  logo?: ReactNode;
  name: ReactNode;
  /** One line of context ("Healthcare, Tampere"). */
  context?: ReactNode;
  href?: string;
  /** The ranked dimension on a scale shared by every row, 0 to 1. Never the raw Employer Score. */
  value: number;
  /** Words for the bar ("Pay: 82nd percentile"). */
  valueText: string;
  /** A GradeBadge. */
  grade?: ReactNode;
  className?: string;
};

/** One ranked employer: the employer is the hero, the rank number stays muted, bars compare across rows. */
export function RankingRow({ rank, logo, name, context, href, value, valueText, grade, className }: RankingRowProps) {
  const title = href ? (
    <a href={href} className="after:absolute after:inset-0 hover:underline focus-visible:outline-none">
      {name}
    </a>
  ) : (
    name
  );
  const bar = (where: string) => (
    <span role="img" aria-label={valueText} className={cx('overflow-hidden rounded-jc-info bg-jc-elevated', where)}>
      <span className="block h-full rounded-jc-info bg-jc-ink" style={{ width: barWidth(value) }} />
    </span>
  );
  return (
    // A container, so the bar's place follows the row's own width (a narrow rail or a phone), not the screen's.
    <div
      className={cx(
        '@container relative focus-within:ring-2 focus-within:ring-inset focus-within:ring-jc-focus',
        href && 'transition-colors duration-(--jc-duration-fast) hover:bg-jc-elevated',
        className,
      )}
    >
      <div className="flex min-h-16 items-center gap-3 px-4 py-2">
        <span className="w-7 shrink-0 text-right type-heading tabular-nums text-jc-ink-secondary">{rank}</span>
        {logo ? <span className="flex shrink-0">{logo}</span> : null}
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate type-subheading text-jc-ink">{title}</span>
          {context ? <span className="truncate text-sm text-jc-ink-secondary">{context}</span> : null}
          {/* Narrow rows: the bar moves under the name so every row still compares on the shared scale. */}
          {bar('mt-1.5 block h-1.5 w-full @md:hidden')}
        </span>
        {bar('hidden h-2.5 w-32 shrink-0 @md:block')}
        {grade ? <span className="flex shrink-0">{grade}</span> : null}
      </div>
    </div>
  );
}
